import { checkQuotaStatus } from "./api";
import { match } from "ts-pattern";

interface QuotaStatus {
  hasQuota: boolean;
  remainingMinutes: number;
}

interface QuotaConfig {
  apiUrl: string;
  domain: string;
  checkInterval?: number; // Optional periodic checking
}

type QuotaCheckResult =
  | { status: "available"; remainingMinutes: number }
  | { status: "exceeded"; remainingMinutes: number }
  | { status: "error"; fallbackToAvailable: true };

class QuotaService {
  private config: QuotaConfig;
  private currentStatus: QuotaCheckResult | null = null;
  private debug: boolean = false;

  constructor(config: QuotaConfig, debug: boolean = false) {
    this.config = config;
    this.debug = debug;
  }

  async checkQuota(): Promise<QuotaCheckResult> {
    try {
      this.log("Checking quota for domain:", this.config.domain);

      const status = await checkQuotaStatus(
        this.config.apiUrl,
        this.config.domain
      );

      this.log("Quota status:", status);

      const result = match(status)
        .with({ hasQuota: true }, ({ remainingMinutes }) => ({
          status: "available" as const,
          remainingMinutes,
        }))
        .with({ hasQuota: false }, ({ remainingMinutes }) => ({
          status: "exceeded" as const,
          remainingMinutes,
        }))
        .exhaustive();

      this.currentStatus = result;
      if (result.status === "exceeded") {
        this.log("Quota exceeded - widget should be hidden");
      }
      return result;
    } catch (error) {
      this.log("Failed to check quota:", error);
      // On error, assume we have quota to avoid blocking
      const fallbackStatus: QuotaCheckResult = {
        status: "error",
        fallbackToAvailable: true,
      };
      this.currentStatus = fallbackStatus;
      return fallbackStatus;
    }
  }

  getCurrentStatus(): QuotaCheckResult | null {
    return this.currentStatus;
  }

  isCheckComplete(): boolean {
    return this.currentStatus !== null;
  }

  hasQuota(): boolean {
    return this.currentStatus?.status !== "exceeded";
  }

  getRemainingMinutes(): number {
    return this.currentStatus && this.currentStatus.status !== "error"
      ? this.currentStatus.remainingMinutes
      : 0;
  }

  private log(...args: any[]): void {
    if (this.debug) {
      console.log("[QuotaService]", ...args);
    }
  }
}

export default QuotaService;
export type { QuotaStatus, QuotaConfig, QuotaCheckResult };
